import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';

// Main container for the background
const BackgroundContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: -1;
  background: linear-gradient(135deg, #1a3328 0%, #0d1f19 100%);
`;

// Floating city text element with stable animation
const FloatingCity = styled.div<{ 
  size: number;
  speed: number;
  delay: number;
  left: number;
  top: number;
  isHighlighted: boolean;
  direction: number;
  visible: boolean;
}>`
  position: absolute;
  color: ${props => props.isHighlighted 
    ? 'rgba(74, 222, 128, 0.9)' 
    : 'rgba(255, 255, 255, 0.15)'};
  font-size: ${props => props.size}px;
  font-weight: ${props => props.isHighlighted ? 'bold' : 'normal'};
  white-space: nowrap;
  opacity: ${props => props.visible 
    ? (props.isHighlighted ? 0.9 : 0.4) 
    : 0};
  text-shadow: ${props => props.isHighlighted 
    ? '0 0 10px rgba(74, 222, 128, 0.5)' 
    : 'none'};
  user-select: none;
  pointer-events: none;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  transition: opacity 1.5s ease, color 1s ease, text-shadow 1s ease;
  
  /* Using simpler animation without keyframes for more fluid motion */
  animation: float-${props => props.direction} ${props => props.speed}s infinite alternate ease-in-out;
  animation-delay: ${props => props.delay}s;
  
  @keyframes float-1 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(60px, 40px);
    }
  }
  
  @keyframes float-2 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(-60px, 40px);
    }
  }
  
  @keyframes float-3 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(60px, -40px);
    }
  }
  
  @keyframes float-4 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(-60px, -40px);
    }
  }
`;

// Interface for props
interface MapBackgroundProps {
  interactive?: boolean;
}

// City configuration interface
interface CityConfig {
  name: string;
  size: number;
  speed: number;
  delay: number;
  left: number;
  top: number;
  direction: number;
}

const MapBackground: React.FC<MapBackgroundProps> = ({ interactive = true }) => {
  const [highlightedCities, setHighlightedCities] = useState<string[]>([]);
  const [visibleCities, setVisibleCities] = useState<string[]>([]);
  const [cityTimers, setCityTimers] = useState<Record<string, number>>({});

  // List of English cities and towns - expanded with more towns
  const englishCities = useMemo(() => [
    // Major cities
    'London', 'Manchester', 'Birmingham', 'Liverpool', 'Leeds', 'Sheffield', 
    'Newcastle', 'Nottingham', 'Bristol', 'Southampton', 'Plymouth', 'Norwich',
    'Oxford', 'Cambridge', 'York', 'Bath', 'Durham', 'Chester', 'Exeter', 'Canterbury',
    
    // Medium cities and towns
    'Brighton', 'Portsmouth', 'Leicester', 'Coventry', 'Hull', 'Stoke-on-Trent',
    'Wolverhampton', 'Derby', 'Swansea', 'Reading', 'Northampton', 'Luton',
    'Milton Keynes', 'Bournemouth', 'Preston', 'Sunderland', 'Southend-on-Sea',
    
    // Railway network cities
    'Crewe', 'Doncaster', 'Didcot Parkway', 'Peterborough', 'Stafford', 'Warrington',
    'Rugby', 'Swindon', 'Watford', 'Basingstoke', 'St Albans', 'Welwyn Garden City',
    
    // Smaller towns
    'Guildford', 'Colchester', 'Chelmsford', 'Harrogate', 'Shrewsbury', 'Ipswich',
    'Carlisle', 'Lincoln', 'Worcester', 'Gloucester', 'Blackpool', 'Wigan',
    'Bolton', 'Oldham', 'Rochdale', 'Blackburn', 'Burnley', 'Huddersfield',
    'Grimsby', 'Scunthorpe', 'Barnsley', 'Rotherham', 'Chesterfield', 'Mansfield',
    
    // Additional towns
    'Warwick', 'Stratford-upon-Avon', 'Leamington Spa', 'Margate', 'Hastings', 'Eastbourne',
    'Folkestone', 'Deal', 'Dover', 'Whitstable', 'Herne Bay', 'Ramsgate', 'Broadstairs',
    'Windsor', 'Maidenhead', 'Slough', 'High Wycombe', 'Aylesbury', 'Bedford', 'Hitchin',
    'Letchworth', 'Stevenage', 'Hatfield', 'Potters Bar', 'Enfield', 'Bromley', 'Croydon',
    'Kingston', 'Richmond', 'Twickenham', 'Ealing', 'Harrow', 'Uxbridge', 'Staines',
    'Woking', 'Aldershot', 'Farnborough', 'Basingstoke', 'Andover', 'Salisbury', 'Poole',
    'Dorchester', 'Weymouth', 'Yeovil', 'Taunton', 'Bridgwater', 'Weston-super-Mare',
    'Chippenham', 'Trowbridge', 'Frome', 'Cirencester', 'Stroud', 'Cheltenham', 'Hereford',
    'Leominster', 'Ludlow', 'Telford', 'Kidderminster', 'Redditch', 'Bromsgrove',
    'Tamworth', 'Lichfield', 'Burton upon Trent', 'Uttoxeter', 'Ashbourne', 'Matlock',
    'Buxton', 'Macclesfield', 'Congleton', 'Northwich', 'Wilmslow', 'Knutsford',
    'Altrincham', 'Sale', 'Stockport', 'Hyde', 'Glossop', 'Ashton-under-Lyne',
    'Stalybridge', 'Saddleworth', 'Halifax', 'Dewsbury', 'Batley', 'Wakefield',
    'Pontefract', 'Castleford', 'Selby', 'Goole', 'Beverley', 'Bridlington',
    'Scarborough', 'Whitby', 'Guisborough', 'Redcar', 'Hartlepool', 'Darlington'
  ], []);

  // Generate random city configurations - only once with useMemo
  const cityConfigs = useMemo(() => {
    // Create a deterministic seed function to ensure consistent values
    const generateSeededRandom = (seed: number) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    
    return englishCities.map((city, i): CityConfig => ({
      name: city,
      size: generateSeededRandom(i * 1.1) * 15 + 10, // Size between 10-25px
      speed: generateSeededRandom(i * 2.3) * 10 + 10, // Animation speed between 10-20s (faster)
      delay: generateSeededRandom(i * 3.7) * 5, // Delay start by 0-5s
      left: generateSeededRandom(i * 4.5) * 85 + 5, // Position between 5-90% of screen width
      top: generateSeededRandom(i * 5.9) * 85 + 5, // Position between 5-90% of screen height
      direction: Math.floor(generateSeededRandom(i * 6.3) * 4) + 1, // Direction 1-4
    }));
  }, [englishCities]);

  // Periodically highlight different cities
  useEffect(() => {
    const highlightInterval = setInterval(() => {
      // Choose 5-8 random cities to highlight
      const numToHighlight = Math.floor(Math.random() * 4) + 5;
      const newHighlighted: string[] = [];
      
      // Use a set to avoid duplicates
      const selectedIndices = new Set<number>();
      while (selectedIndices.size < numToHighlight) {
        const index = Math.floor(Math.random() * englishCities.length);
        selectedIndices.add(index);
      }
      
      // Convert set of indices to array of city names
      Array.from(selectedIndices).forEach(index => {
        newHighlighted.push(englishCities[index]);
      });
      
      setHighlightedCities(newHighlighted);
    }, 3000); // Slightly shorter interval for more frequent highlights
    
    return () => clearInterval(highlightInterval);
  }, [englishCities]);

  // Function to add new cities
  const addNewCities = useCallback(() => {
    // Add 8-12 random cities
    const numToAdd = Math.floor(Math.random() * 5) + 8;
    const newCities: string[] = [];
    
    // Track which cities are already visible
    const currentVisible = new Set(visibleCities);
    
    // Add random cities that aren't already visible
    let attempts = 0;
    while (newCities.length < numToAdd && attempts < 100) {
      attempts++;
      const index = Math.floor(Math.random() * englishCities.length);
      const cityName = englishCities[index];
      
      if (!currentVisible.has(cityName)) {
        newCities.push(cityName);
        currentVisible.add(cityName);
      }
    }
    
    // Update the visible cities
    setVisibleCities(prev => [...prev, ...newCities]);
    
    // Set timers for the new cities
    const now = Date.now();
    setCityTimers(prev => {
      const newTimers = { ...prev };
      newCities.forEach(city => {
        newTimers[city] = now;
      });
      return newTimers;
    });
    
    // Randomly highlight some of the new cities (25% chance per city)
    const newHighlighted = newCities.filter(() => Math.random() < 0.25);
    if (newHighlighted.length > 0) {
      setHighlightedCities(prev => [...prev, ...newHighlighted]);
    }
  }, [visibleCities, englishCities]);

  // Function to remove old cities
  const removeOldCities = useCallback(() => {
    const now = Date.now();
    const tenSecondsAgo = now - 10000; // 10 seconds ago
    
    // Find cities that have been visible for more than 10 seconds
    const citiesToRemove: string[] = [];
    
    Object.entries(cityTimers).forEach(([city, timestamp]) => {
      if (timestamp < tenSecondsAgo) {
        citiesToRemove.push(city);
      }
    });
    
    // Remove the cities from visible list
    if (citiesToRemove.length > 0) {
      setVisibleCities(prev => prev.filter(city => !citiesToRemove.includes(city)));
      
      // Remove from timers
      setCityTimers(prev => {
        const newTimers = { ...prev };
        citiesToRemove.forEach(city => {
          delete newTimers[city];
        });
        return newTimers;
      });
      
      // Also remove from highlighted if needed
      setHighlightedCities(prev => prev.filter(city => !citiesToRemove.includes(city)));
    }
  }, [cityTimers]);

  // Manage city visibility
  useEffect(() => {
    // Start with more initial cities
    if (visibleCities.length === 0) {
      const initialCities = englishCities.slice(0, 40); // Start with 40 cities
      setVisibleCities(initialCities);
      
      const now = Date.now();
      const initialTimers: Record<string, number> = {};
      initialCities.forEach(city => {
        initialTimers[city] = now;
      });
      setCityTimers(initialTimers);
      
      // Highlight some initial cities
      const initialHighlighted = initialCities.filter(() => Math.random() < 0.2); // 20% chance
      setHighlightedCities(initialHighlighted);
    }
    
    // Set up intervals for adding and removing cities
    const addInterval = setInterval(addNewCities, 2000); // Add new cities every 2 seconds
    const removeInterval = setInterval(removeOldCities, 1000); // Check for old cities every 1 second
    
    return () => {
      clearInterval(addInterval);
      clearInterval(removeInterval);
    };
  }, [englishCities, addNewCities, removeOldCities, visibleCities.length]);

  return (
    <BackgroundContainer>
      {cityConfigs.map((config) => (
        <FloatingCity
          key={`city-${config.name}`}
          size={config.size}
          speed={config.speed}
          delay={config.delay}
          left={config.left}
          top={config.top}
          direction={config.direction}
          isHighlighted={highlightedCities.includes(config.name)}
          visible={visibleCities.includes(config.name)}
        >
          {config.name}
        </FloatingCity>
      ))}
    </BackgroundContainer>
  );
};

export default MapBackground; 